import { useRef, useCallback, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Vector3 } from "three";
import { getModelConfig } from "@/lib/models";

interface CameraFocusState {
  target: Vector3 | null;
  startPosition: Vector3;
  startTarget: Vector3;
  endPosition: Vector3;
  endTarget: Vector3;
  progress: number;
  isAnimating: boolean;
  duration: number;
}

export const useCameraFocus = (animationDuration: number = 1000, modelName?: string) => {
  const { camera } = useThree();
  const controlsRef = useRef<any>(null);
  
  // 根据模型名称获取相机配置
  const modelConfig = modelName ? getModelConfig(modelName) : null;
  const defaultCameraPosition = modelConfig?.camera.position || new Vector3(2, 2, 3);
  const defaultCameraTarget = modelConfig?.camera.target || new Vector3(0, 0, 0);
  const defaultFocusDistance = modelConfig?.camera.focusDistance || 0.8;
  
  const animationState = useRef<CameraFocusState>({
    target: null,
    startPosition: new Vector3(),
    startTarget: new Vector3(),
    endPosition: new Vector3(),
    endTarget: new Vector3(),
    progress: 0,
    isAnimating: false,
    duration: animationDuration,
  });

  // 设置OrbitControls引用并初始化相机位置
  const setControlsRef = useCallback((controls: any) => {
    controlsRef.current = controls;
    
    // 每次OrbitControls重新创建时使用动画平滑过渡到正确位置
    if (controls) {
      // 稍微延迟以确保controls完全初始化
      setTimeout(() => {
        if (!controlsRef.current) return;
        
        const state = animationState.current;
        
        // 设置起始状态（当前相机位置）
        state.startPosition.copy(camera.position);
        state.startTarget.copy(controls.target);
        
        // 设置目标状态（配置的默认位置）
        state.endPosition.copy(defaultCameraPosition);
        state.endTarget.copy(defaultCameraTarget);
        
        // 开始动画
        state.isAnimating = true;
        state.progress = 0;
        state.target = null;
      }, 50);
    }
  }, [camera, defaultCameraPosition, defaultCameraTarget]);

  // 缓动函数
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  // 聚焦到指定点
  const focusToPoint = useCallback((point: Vector3, distance: number = 2) => {
    if (!controlsRef.current) return;

    const controls = controlsRef.current;
    const state = animationState.current;

    // 设置起始状态
    state.startPosition.copy(camera.position);
    state.startTarget.copy(controls.target);

    // 使用更稳定的方法：从当前相机位置到点击点的方向
    const cameraDirection = camera.position.clone().sub(point).normalize();
    
    // 计算新的相机位置
    state.endPosition.copy(point).add(cameraDirection.multiplyScalar(distance));
    state.endTarget.copy(point);

    // 开始动画
    state.isAnimating = true;
    state.progress = 0;
    state.target = point;
  }, [camera]);

  // 回到默认视角
  const resetCamera = useCallback(() => {
    if (!controlsRef.current) return;

    const controls = controlsRef.current;
    const state = animationState.current;

    // 设置起始状态
    state.startPosition.copy(camera.position);
    state.startTarget.copy(controls.target);

    // 设置默认结束状态 - 使用配置的默认位置
    state.endPosition.copy(defaultCameraPosition);
    state.endTarget.copy(defaultCameraTarget);

    // 开始动画
    state.isAnimating = true;
    state.progress = 0;
    state.target = null;
  }, [camera, defaultCameraPosition, defaultCameraTarget]);

  // 动画循环
  useFrame((_, delta) => {
    const state = animationState.current;
    
    if (!state.isAnimating || !controlsRef.current) return;

    const controls = controlsRef.current;
    
    // 更新进度
    state.progress += (delta * 1000) / state.duration;
    
    if (state.progress >= 1) {
      // 动画完成
      state.progress = 1;
      state.isAnimating = false;
      
      camera.position.copy(state.endPosition);
      controls.target.copy(state.endTarget);
      controls.update();
      return;
    }

    // 应用缓动
    const easedProgress = easeInOutCubic(state.progress);
    
    // 插值位置和目标
    const currentPosition = state.startPosition.clone().lerp(state.endPosition, easedProgress);
    const currentTarget = state.startTarget.clone().lerp(state.endTarget, easedProgress);
    
    // 应用到相机和控制器
    camera.position.copy(currentPosition);
    controls.target.copy(currentTarget);
    controls.update();
  });

  return {
    focusToPoint,
    resetCamera,
    setControlsRef,
    isAnimating: animationState.current.isAnimating,
    defaultFocusDistance,
  };
};